import type { Profile } from '@parentpath/supabase-client';

export const REVENUECAT_PRODUCT_LABELS: Record<string, string> = {
  pp_monthly: 'Monatsabo',
  pp_6month: '6-Monats-Abo',
  pp_1year: 'Jahresabo',
};

/**
 * Liefert ein menschenlesbares Label für die Premium-Quelle eines Users.
 *
 * Reihenfolge der Prüfung:
 *   1. is_lifetime → "Lifetime" (vor allen anderen Sources)
 *   2. premium_source → spezifisches Label
 *   3. Fallback "Kein Premium"
 */
export function formatPremiumSource(profile: Profile): string {
  // Lifetime hat Vorrang vor allen anderen Sources
  if (profile.is_lifetime) {
    return 'Lifetime';
  }

  switch (profile.premium_source) {
    case 'apple': {
      const product = REVENUECAT_PRODUCT_LABELS[profile.revenuecat_product_id ?? ''] ?? 'IAP';
      return `Apple · ${product}`;
    }
    case 'google': {
      const product = REVENUECAT_PRODUCT_LABELS[profile.revenuecat_product_id ?? ''] ?? 'IAP';
      return `Google Play · ${product}`;
    }
    case 'stripe':
      return 'Stripe (Web)';
    case 'beta_code':
      return 'Beta-Code';
    case 'promo_code':
      return 'Promo-Code';
    case 'admin_grant':
      return 'Admin gewährt';
    default:
      return 'Kein Premium';
  }
}

/**
 * Grobe Einordnung des Premium-Status fuer die Uebersichtsliste.
 *
 *   'none'  - kein Premium
 *   'paid'  - bezahltes Abo: apple/google (IAP) und stripe (Web-Checkout).
 *             Stripe liegt hier, weil es derselbe Vorgang ist - der Nutzer
 *             hat bezahlt, nur ueber einen anderen Kanal (stripe-checkout /
 *             stripe-webhook laufen produktiv, siehe supabase/functions).
 *   'code'  - Premium ohne Zahlung: beta_code, promo_code und admin_grant
 *             (das ist die "Kostenloses Premium gewaehren"-Aktion aus dem
 *             Detail-Modal, also ebenfalls geschenktes Premium).
 *
 * is_lifetime wird bewusst nicht ausgewertet: Lifetime kann sowohl aus einem
 * Code als auch aus einem Kauf stammen - die Quelle bleibt massgeblich.
 */
export type PremiumGroup = 'none' | 'paid' | 'code';

export function getPremiumGroup(profile: Pick<Profile, 'premium_source'>): PremiumGroup {
  switch (profile.premium_source) {
    case 'apple':
    case 'google':
    case 'stripe':
      return 'paid';
    case 'beta_code':
    case 'promo_code':
    case 'admin_grant':
      return 'code';
    default:
      return 'none';
  }
}
